import { ReactNode, useEffect, useState } from 'react';
import {
  PaymentMethodConfig,
  PaymentMethodConfigType,
  PaymentMethodStatus,
} from '../../../clients/quipli-service/payment-config/payment-config';
import { usePaymentConfigApiV1 } from '../../../clients/quipli-service/payment-config/payment-config-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { PaymentMethodModal } from './payment-method-modal';
import Icon from '../../../components/icons/icon';
import { Button } from '../../../components/form/button';
import { DataTable, DataTableRow } from '../../../components/table/data-table';
import { DataTableColumn } from '../../../components/sortable-table/data-table';
import { Typography } from '../../../components/typography/typography';
import { Tooltip } from '../../../components/tooltip/tooltip';

export const PaymentMethodConfiguration = () => {
  const [, setPaymentMethods] = useState<PaymentMethodConfig[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [methodModal, setMethodModal] = useState<ReactNode | undefined>();
  const [tableData, setTableData] = useState<DataTableRow[]>([]);

  const { searchPaymentMethods, deletePaymentMethod, updatePaymentMethod } = usePaymentConfigApiV1();

  async function init() {
    setIsLoading(true);
    const pms = await searchPaymentMethods({});
    setPaymentMethods(pms);
    setTableData(
      pms
        .sort((a, b) => {
          return ('' + a.displayName).localeCompare(b.displayName);
        })
        .map(buildDataRow),
    );
    setIsLoading(false);
  }

  useEffect(() => {
    init();
  }, []);

  const columns: DataTableColumn[] = [
    { key: 'displayName', label: 'Name', showRule: 'OnlyDesktop' },
    { key: 'description', label: 'Description', showRule: 'OnlyDesktop' },
    { key: 'actions', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
  ];

  const buildDataRow = (paymentMethod: PaymentMethodConfig) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight={'medium'} className={'text-quipli-black-800'}>
            {paymentMethod.displayName}
          </Typography>
        </>
      ),
      mobileHeaderAction: <PaymentMethodActions paymentMethod={paymentMethod} />,
      fields: {
        displayName: (
          <Typography type="text-md" weight={'medium'} className={'text-quipli-black-800'}>
            {paymentMethod.displayName}
          </Typography>
        ),
        description: (
          <Typography type="text-md" weight={'medium'} className={'text-quipli-black-800'}>
            {paymentMethod.description}
          </Typography>
        ),
        actions: <PaymentMethodActions paymentMethod={paymentMethod} />,
      },
    };
  };

  const PaymentMethodActions = ({ paymentMethod }: { paymentMethod: PaymentMethodConfig }) => {
    return (
      <div className={'flex flex-row'}>
        {paymentMethod.type == PaymentMethodConfigType.Custom ? (
          <>
            <div
              onClick={() =>
                setMethodModal(
                  <PaymentMethodModal
                    isOpen={true}
                    close={() => setMethodModal(undefined)}
                    paymentMethod={paymentMethod}
                  />,
                )
              }
            >
              <Icon icon={'Edit'}></Icon>
            </div>
            <div className={'md:block hidden'}>
              <Icon icon={'Trash'} onClick={async () => deletePaymentMethod(paymentMethod.id)}></Icon>
            </div>
          </>
        ) : (
          <>
            {paymentMethod.status == 'Active' ? (
              <Tooltip tooltip={'Disable payment method'} type={'black'}>
                <div
                  onClick={async () => {
                    await updatePaymentMethod({ ...paymentMethod, status: PaymentMethodStatus.Inactive });
                    await init();
                  }}
                >
                  <Icon icon={'X'}></Icon>
                </div>
              </Tooltip>
            ) : (
              <Tooltip tooltip={'Enable payment method'} type={'black'}>
                <div
                  onClick={async () => {
                    await updatePaymentMethod({
                      ...paymentMethod,
                      status: PaymentMethodStatus.Active,
                    });
                    await init();
                  }}
                >
                  <Icon icon={'Check'}></Icon>
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <h1>Manage Payment Methods</h1>
      {!isLoading ? (
        <div className={'gap-2 p-2 flex flex-col'}>
          <div className="hidden md:block">
            <div className="flex flex-end">
              <Button
                className="button-yellow sm"
                data-cy="add_custom_method"
                onClick={() =>
                  setMethodModal(
                    <PaymentMethodModal
                      isOpen={true}
                      close={async () => {
                        setMethodModal(undefined);
                        await init();
                      }}
                    />,
                  )
                }
              >
                <i className="fa fa-plus" />
                Create Payment Method
              </Button>
            </div>
          </div>
          <DataTable columnNames={columns} isLoading={isLoading} data={tableData}></DataTable>
        </div>
      ) : (
        <Spinner />
      )}
      {methodModal}
    </div>
  );
};
